'use client'

/**
 * InteractiveBarChart — the interactive explorer companion for the Fridge.com
 * /data asset pages (price-drops, capacity-trends, wine-beverage-index,
 * rebate-value-index, brand-recall-history).
 *
 * Built on recharts (already a dependency), following the pattern set by
 * components/data/interactive-trend-chart.tsx: it reads the SAME published
 * rows the static SVG figures and tables render, so the two can never
 * disagree, and adds client interactivity on top:
 *   • a metric switcher — toggle which published measure the bars plot
 *   • a sort control — by value or A–Z
 *   • rich hover tooltips showing every metric for the hovered row
 *   • the shared per-figure toolbar (Download PNG + ShareBar) via FigureActions
 *
 * Hydration-safe: recharts measures the DOM, so the chart renders only after
 * mount behind a fixed-height placeholder. Light background, Fridge.com
 * palette, responsive at every width.
 */
import { useMemo, useState, useEffect } from 'react'
import { FigureBrandMark } from "./figure-brand-mark"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { FigureActions } from '@/app/data/_components/figure-actions'
import { useIsHydrated } from '@/hooks/use-is-hydrated'

/**
 * How a metric's raw number is rendered on axis ticks and in tooltips.
 *
 * A NAME, never a function. This component is `'use client'`, and the only
 * caller is a Server Component — so a `format: (n) => …` prop is not a style
 * choice, it is a hard build error ("Functions cannot be passed directly to
 * Client Components") that fails `next build` on `/data/price-index` and would
 * fail the production deploy. Names serialize; closures do not.
 *
 * Add a case here and to FORMATTERS below when a page needs a new one.
 */
export type BarMetricFormat =
  /** `$1,234.56` — the value is in DOLLARS (formatDollars takes cents). */
  | 'dollars'
  /** `101.4` — one decimal place. */
  | 'decimal1'
  /** `1,234` — thousands-separated whole number. */
  | 'integer'

export const BAR_METRIC_FORMATTERS: Record<BarMetricFormat, (n: number) => string> = {
  dollars: (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  decimal1: (n) => n.toFixed(1),
  integer: (n) => n.toLocaleString('en-US'),
}

export interface BarMetric {
  key: string
  label: string
  /** Named formatter for axis ticks and tooltips — see BarMetricFormat. */
  format: BarMetricFormat
}

export interface BarRow {
  label: string
  /** Numeric values keyed by metric key. */
  values: Record<string, number>
  /** Optional accent — highlighted rows render in Fridge orange. */
  highlight?: boolean
  /** Live collection / brand page for this row (FRIDGE-334); drives the linked legend. */
  href?: string | null
}

interface InteractiveBarChartProps {
  title: string
  rows: BarRow[]
  metrics: BarMetric[]
  /** Section anchor id for the shareable deep link. */
  anchorId?: string
  /** Download filename stem for the PNG export. */
  downloadSlug: string
  /** Visible caption below the chart. */
  caption: string
  /** Bar height per row in px (chart height scales with row count). */
  rowHeight?: number
}

type SortMode = 'value' | 'alpha'

export function InteractiveBarChart({
  title,
  rows,
  metrics,
  anchorId,
  downloadSlug,
  caption,
  rowHeight = 34,
}: InteractiveBarChartProps) {
  const mounted = useIsHydrated()
  const [metricKey, setMetricKey] = useState(metrics[0]?.key ?? '')
  const [sort, setSort] = useState<SortMode>('value')

  const metric = metrics.find((m) => m.key === metricKey) ?? metrics[0]

  const data = useMemo(() => {
    const mapped = rows.map((r) => ({
      label: r.label,
      value: r.values[metric.key] ?? 0,
      highlight: !!r.highlight,
      all: r.values,
    }))
    if (sort === 'alpha') mapped.sort((a, b) => a.label.localeCompare(b.label))
    else mapped.sort((a, b) => b.value - a.value)
    return mapped
  }, [rows, metric.key, sort])

  const height = Math.max(rows.length * rowHeight + 40, 180)

  return (
    <figure className="m-0 bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 sm:px-6 py-3.5 border-b border-gray-200 flex flex-col gap-y-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:flex-wrap">
        <h3 className="text-sm sm:text-[15px] font-bold tracking-tight text-gray-900 m-0">
          {title}
        </h3>
        <FigureActions slug={downloadSlug} shareTitle={title} anchorId={anchorId} />
      </div>

      {/* Controls: metric switcher + sort toggle */}
      <div className="px-5 sm:px-6 pt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        {metrics.length > 1 && (
          <div
            className="inline-flex flex-wrap items-center gap-1 rounded-full border border-gray-200 p-0.5"
            role="group"
            aria-label="Metric"
          >
            {metrics.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMetricKey(m.key)}
                aria-pressed={metric.key === m.key}
                className={`text-[12px] font-bold px-3 h-7 rounded-full transition-colors ${
                  metric.key === m.key
                    ? 'bg-[#0469f0] text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
        <div
          className="inline-flex items-center gap-1 rounded-full border border-gray-200 p-0.5"
          role="group"
          aria-label="Sort order"
        >
          {(
            [
              { key: 'value', label: 'By value' },
              { key: 'alpha', label: 'A–Z' },
            ] as Array<{ key: SortMode; label: string }>
          ).map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSort(s.key)}
              aria-pressed={sort === s.key}
              className={`text-[12px] font-bold px-3 h-7 rounded-full transition-colors ${
                sort === s.key ? 'bg-[#0469f0] text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-2 sm:px-4 py-4">
        <div style={{ width: '100%', height }}>
          {mounted ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 4, right: 28, bottom: 4, left: 8 }}
              >
                <CartesianGrid stroke="#eef1f4" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(v: number) => BAR_METRIC_FORMATTERS[metric.format](v)}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={128}
                  tick={{ fill: '#374151', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(4,105,240,0.05)' }}
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid #e5e7eb',
                    fontSize: 12,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                  }}
                  formatter={() => null}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null
                    const row = payload[0].payload as (typeof data)[number]
                    return (
                      <div className="rounded-xl border border-gray-200 bg-white shadow-lg px-3.5 py-2.5 text-[12px]">
                        <p className="font-bold text-gray-900 m-0 mb-1">{row.label}</p>
                        {metrics.map((m) => (
                          <p key={m.key} className="m-0 text-gray-600">
                            {m.label}:{' '}
                            <span
                              className={`font-bold ${m.key === metric.key ? 'text-[#0469f0]' : 'text-gray-900'}`}
                            >
                              {BAR_METRIC_FORMATTERS[m.format](row.all[m.key] ?? 0)}
                            </span>
                          </p>
                        ))}
                      </div>
                    )
                  }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  {data.map((d) => (
                    <Cell key={d.label} fill={d.highlight ? '#ff9951' : '#0469f0'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full rounded-lg bg-gray-50 animate-pulse" aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Linked legend — recharts labels are SVG text, so each plotted row that
          has a live page gets a real link here (FRIDGE-334). */}
      {rows.some((r) => r.href) && (
        <ul className="px-5 sm:px-6 pb-4 flex flex-wrap gap-2 list-none m-0" aria-label="Browse what is plotted">
          {rows.map((r) =>
            r.href ? (
              <li key={r.label}>
                <Link
                  href={r.href}
                  prefetch={false}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[12px] font-semibold text-gray-900 hover:border-[#ff9951] hover:text-[#0469f0] transition-colors"
                  title={`Browse ${r.label} on Fridge.com`}
                >
                  {r.label}
                  <ArrowUpRight className="w-3 h-3 text-[#ff9951]" aria-hidden="true" />
                </Link>
              </li>
            ) : null
          )}
        </ul>
      )}

      {/* FRIDGE-591: the brand mark lives INSIDE the figure so any screenshot
          of the chart carries Fridge.com with it. */}
      <figcaption className="px-5 sm:px-6 py-3 border-t border-gray-100 bg-[#0469f0]/[0.03] text-[13px] text-gray-600 leading-relaxed flex items-start justify-between gap-4">
        <span>{caption}</span>
        <FigureBrandMark />
      </figcaption>
    </figure>
  )
}
